import type { RouteRecordRaw } from "vue-router"
import {
  useApplicationStore,
  useCurrentProviderConfig,
  useCurrentProviderKey,
  usePreauthResponse,
  useProviderConfig,
  useProviderStore,
  useRequireCurrentProviderKey,
} from "@versatile-credit-apply/sdk"

import {
  useApplyMode,
  usePrimaryProvider,
} from "@versatile-credit-apply/composition"
import { useApplyNavigationPath } from "@/composition"

export const SnapDecisionRoutes: RouteRecordRaw[] = [
  {
    name: "snap-apply-approved",
    path: "approved",
    component: () => import("./components/SnapApproved.vue"),
  },
  {
    name: "snap-apply-declined",
    path: "declined",
    component: () => import("./components/SnapDeclined.vue"),
  },
  {
    name: "snap-apply-nooffer",
    path: "nooffer",
    component: () => import("./components/SnapNoOffer.vue"),
  },
  {
    name: "snap-apply-pending",
    path: "pending",
    component: () => import("./components/SnapPending.vue"),
  },
  {
    name: "snap-apply-prequalified",
    path: "prequalified",
    component: () => import("./components/SnapPrequalified.vue"),
    meta: {
      next: () => {
        if (isSlnPrequalCascade()) {
          return `../product-knowledge`
        }

        return "../application"
      },
      step: "reviewOffer",
    },
  },
  {
    name: "snap-apply-error",
    path: "error",
    component: () => import("./components/SnapError.vue"),
  },
]

export const SnapApplyRoutes: RouteRecordRaw[] = [
  {
    name: "snap-apply-routes",
    path: "snap_standard",
    children: [
      {
        name: "snap-prequalify-start",
        path: "start/prequalify",
        component: () => import("./pages/SnapIntroPage.vue"),
        beforeEnter: () => {
          const providerKey = useRequireCurrentProviderKey()
          const preauthResponse = usePreauthResponse(providerKey)

          // snap sln cascade redirect
          if (
            !preauthResponse.value &&
            isNotPrimaryLender() &&
            isSlnProduct()
          ) {
            return "../application/prefilled"
          }
        },
        meta: {
          step: "applicant",
        },
      },
      {
        name: "snap-apply-start",
        path: "start/:mode",
        component: () => import("./pages/SnapIntroPage.vue"),
        meta: {
          step: "applicant",
        },
      },
      {
        name: "snap-product-knowledge",
        path: "product-knowledge",
        component: () => import("./pages/SnapIntroPage.vue"),
        beforeEnter: (to) => {
          const providerKey = useRequireCurrentProviderKey()
          const preauthResponse = usePreauthResponse(providerKey)

          if (preauthResponse.value && isSlnPrequalCascade()) {
            to.meta.next = () =>
              otpEnabled() ? "verify-code" : "application/primary/additional"
          }
        },
      },
      {
        name: "snap-terms-redirect",
        path: "terms",
        redirect: () => {
          if (isSlnPrequalCascade()) {
            return "../../snap-terms"
          }

          const providerKey = useRequireCurrentProviderKey()

          if (otpEnabled()) {
            return `/apply/providers/${providerKey.value}/verify-code`
          } else {
            return resolveTermsOrLegalPath()
          }
        },
      },
      {
        name: "snap-verify-code",
        path: "verify-code",
        component: () => import("./pages/SnapVerifyCodePage.vue"),
        meta: {
          next: () => {
            if (isSlnPrequalCascade()) {
              return "application/primary/additional"
            }

            return resolveTermsOrLegalPath()
          },
          step: "applicant",
        },
      },
      {
        name: "snap-terms",
        path: "snap-terms",
        component: () => import("./pages/SnapTermsAndConditionsPage.vue"),
        meta: {
          next: () => {
            const store = useApplicationStore()
            return store.applyMode === "full" ? "submit" : "patch"
          },
          step: "terms",
        },
      },
      {
        name: "snap-legal",
        path: "snap-legal",
        component: () => import("./pages/SnapIntroPage.vue"),
        meta: {
          next: "snap-terms",
          step: "applicant",
        },
      },
      {
        name: "snap-patch-success",
        path: "patch-success",
        redirect: "accept-offer",
      },
      {
        name: "snap-apply-decisions",
        path: "decision",
        component: () => import("./pages/SnapDecisionsPage.vue"),
        meta: {
          preventBack: true,
          step: "decision",
        },
        children: [...SnapDecisionRoutes],
      },
    ],
  },
]
const otpEnabled = () =>
  useCurrentProviderConfig().value?.properties.otpEnabled ?? true

function isNotPrimaryLender() {
  const primaryProvider = usePrimaryProvider()
  const providerKey = useRequireCurrentProviderKey()

  return providerKey.value !== primaryProvider.value?.providerKey
}

function isPrequalCascade() {
  const applyMode = useApplyMode()
  return applyMode.value == "prequalify" && isNotPrimaryLender()
}

function isSlnProduct() {
  const config = useCurrentProviderConfig()
  return config.value?.providerProduct === "snap-bank-loan"
}

function isSlnPrequalCascade() {
  return isPrequalCascade() && isSlnProduct()
}

const resolveTermsOrLegalPath = () => {
  const store = useProviderStore()
  const currentProviderKey = useCurrentProviderKey()
  const config = useProviderConfig(currentProviderKey)
  // If we aren't first in the cascade, and we are autoSubmitting then we didn't
  // see the legal info
  return useApplyNavigationPath(() => {
    if (
      store.primary?.providerKey != currentProviderKey.value &&
      config?.value?.autoSubmit
    ) {
      return "snap-legal"
    } else {
      return "snap-terms"
    }
  }).value
}
